import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Loader2, Users, Download, Upload, FileSpreadsheet, Calendar, Filter, UserMinus, Eye, FileText, Clock, CheckCircle, AlertCircle, Mail, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import RenterForm from './RenterForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RenterManager() {
  const [renters, setRenters] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [dateFilter, setDateFilter] = useState('upcoming');
  const [sortColumn, setSortColumn] = useState('check_in_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rentersRes, contractsRes] = await Promise.all([
        axios.get(`${API}/renters`),
        axios.get(`${API}/contracts`).catch(() => ({ data: [] }))
      ]);
      setRenters(rentersRes.data);
      setContracts(contractsRes.data || []);
    } catch (error) {
      toast.error('Failed to load renters');
    } finally {
      setLoading(false);
    }
  };

  // Get contract status for a renter
  const getContractStatus = (renterId) => {
    const renterContracts = contracts.filter(c => c.renter_id === renterId);
    if (renterContracts.length === 0) {
      return { status: 'not_sent', label: 'No Contract', color: 'bg-gray-100 text-gray-600' };
    }
    
    const signedContract = renterContracts.find(c => c.status === 'signed');
    if (signedContract) {
      return { status: 'signed', label: 'Signed', color: 'bg-green-100 text-green-800' };
    }
    
    const pendingContract = renterContracts.find(c => c.status === 'pending');
    if (pendingContract) {
      return { status: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
    }
    
    return { status: 'expired', label: 'Expired', color: 'bg-red-100 text-red-800' };
  };

  const fetchRenters = async () => {
    try {
      const res = await axios.get(`${API}/renters`);
      setRenters(res.data);
    } catch (error) {
      toast.error('Failed to load renters');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, renter) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${renter.first_name} ${renter.last_name}?`)) return;
    
    try {
      await axios.delete(`${API}/renters/${renter.id}`);
      toast.success('Renter deleted');
      fetchRenters();
    } catch (error) {
      toast.error('Failed to delete renter');
    }
  };

  const handleConvertToInquiry = async (e, renter) => {
    e.stopPropagation();
    if (!window.confirm('Convert this renter back to an inquiry?')) return;
    
    try {
      await axios.post(`${API}/renters/convert-to-inquiry`, { renter_id: renter.id });
      toast.success('Converted to inquiry');
      fetchRenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };

  const handleSendEmail = (e, renter) => {
    e.stopPropagation();
    // Navigate to email client with renter pre-selected
    navigate(`/admin/emails?recipient=${renter.id}&type=renter`);
  };

  const handleFormSave = async (data) => {
    try {
      const response = await axios.post(`${API}/renters`, data);
      toast.success('Renter added');
      setShowAddDialog(false);
      fetchRenters();
      navigate(`/admin/renters/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add renter');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/renters/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `renters_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/renters/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `renters_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/renters/import`, formData);
      toast.success(`Imported ${response.data.imported} renters`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.skipped} rows skipped`);
      }
      fetchRenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }
    
    e.target.value = '';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Toronto'
      });
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // Get unique years from renters data
  const availableYears = useMemo(() => {
    const years = new Set();
    renters.forEach(renter => {
      if (renter.check_in_date) {
        years.add(new Date(renter.check_in_date).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [renters]);

  // Filter and sort renters
  const filteredRenters = useMemo(() => {
    let filtered = renters.filter(renter => {
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        renter.first_name?.toLowerCase().includes(searchLower) ||
        renter.last_name?.toLowerCase().includes(searchLower) ||
        renter.email?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkIn = renter.check_in_date ? new Date(renter.check_in_date) : null;

      switch (dateFilter) {
        case 'upcoming':
          return checkIn && checkIn >= today;
        case '30':
          const thirtyDays = new Date(today);
          thirtyDays.setDate(thirtyDays.getDate() + 30);
          return checkIn && checkIn >= today && checkIn <= thirtyDays;
        case '60':
          const sixtyDays = new Date(today);
          sixtyDays.setDate(sixtyDays.getDate() + 60);
          return checkIn && checkIn >= today && checkIn <= sixtyDays;
        case '90':
          const ninetyDays = new Date(today);
          ninetyDays.setDate(ninetyDays.getDate() + 90);
          return checkIn && checkIn >= today && checkIn <= ninetyDays;
        case 'past':
          return checkIn && checkIn < today;
        case 'all':
          return true;
        default:
          if (!isNaN(parseInt(dateFilter))) {
            return checkIn && checkIn.getFullYear() === parseInt(dateFilter);
          }
          return true;
      }
    });

    // Sort based on column and direction
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortColumn) {
        case 'name':
          comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'check_in_date':
          comparison = new Date(a.check_in_date || 0) - new Date(b.check_in_date || 0);
          break;
        case 'check_out_date':
          comparison = new Date(a.check_out_date || 0) - new Date(b.check_out_date || 0);
          break;
        case 'rental_amount':
          comparison = (a.total_with_hst || a.rental_amount || 0) - (b.total_with_hst || b.rental_amount || 0);
          break;
        case 'amount_paid':
          comparison = (a.amount_paid || 0) - (b.amount_paid || 0);
          break;
        case 'amount_owing':
          const owingA = (a.total_with_hst || a.rental_amount || 0) - (a.amount_paid || 0);
          const owingB = (b.total_with_hst || b.rental_amount || 0) - (b.amount_paid || 0);
          comparison = owingA - owingB;
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [renters, search, dateFilter, sortColumn, sortDirection]);

  // Calculate totals
  const totals = useMemo(() => {
    return filteredRenters.reduce((acc, renter) => {
      const rentalAmount = renter.total_with_hst || renter.rental_amount || 0;
      const amountPaid = renter.amount_paid || 0;
      const amountOwing = rentalAmount - amountPaid;
      
      return {
        rental: acc.rental + rentalAmount,
        paid: acc.paid + amountPaid,
        owing: acc.owing + amountOwing
      };
    }, { rental: 0, paid: 0, owing: 0 });
  }, [filteredRenters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1B4D3E]">Renters</h1>
          <p className="text-gray-500">{filteredRenters.length} of {renters.length} renters</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportExcel}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" /> Import
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".csv,.xlsx,.xls"
            className="hidden"
          />
          <Button onClick={() => setShowAddDialog(true)} className="bg-[#1B4D3E] hover:bg-[#163d32]">
            <Plus className="w-4 h-4 mr-2" /> Add Renter
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="30">Next 30 Days</SelectItem>
            <SelectItem value="60">Next 60 Days</SelectItem>
            <SelectItem value="90">Next 90 Days</SelectItem>
            <SelectItem value="past">Past Rentals</SelectItem>
            <SelectItem value="all">All Rentals</SelectItem>
            {availableYears.map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Renters Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Name {getSortIcon('name')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('check_in_date')}
              >
                <div className="flex items-center">
                  Check-in {getSortIcon('check_in_date')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('check_out_date')}
              >
                <div className="flex items-center">
                  Check-out {getSortIcon('check_out_date')}
                </div>
              </TableHead>
              <TableHead>Contract</TableHead>
              <TableHead 
                className="text-right cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('rental_amount')}
              >
                <div className="flex items-center justify-end">
                  Rental Amount {getSortIcon('rental_amount')}
                </div>
              </TableHead>
              <TableHead 
                className="text-right cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('amount_paid')}
              >
                <div className="flex items-center justify-end">
                  Amount Paid {getSortIcon('amount_paid')}
                </div>
              </TableHead>
              <TableHead 
                className="text-right cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('amount_owing')}
              >
                <div className="flex items-center justify-end">
                  Amount Owing {getSortIcon('amount_owing')}
                </div>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRenters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No renters found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredRenters.map((renter) => {
                const contractStatus = getContractStatus(renter.id);
                const rentalAmount = renter.total_with_hst || renter.rental_amount || 0;
                const amountPaid = renter.amount_paid || 0;
                const amountOwing = rentalAmount - amountPaid;
                
                return (
                <TableRow 
                  key={renter.id} 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/admin/renters/${renter.id}`)}
                >
                  <TableCell>
                    <div>
                      <p className="font-medium text-[#1B4D3E]">
                        {renter.first_name} {renter.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{renter.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      {formatDate(renter.check_in_date)}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(renter.check_out_date)}</TableCell>
                  <TableCell>
                    <Badge className={`${contractStatus.color} flex items-center gap-1 w-fit`}>
                      {contractStatus.status === 'signed' && <CheckCircle className="h-3 w-3" />}
                      {contractStatus.status === 'pending' && <Clock className="h-3 w-3" />}
                      {contractStatus.status === 'not_sent' && <FileText className="h-3 w-3" />}
                      {contractStatus.status === 'expired' && <AlertCircle className="h-3 w-3" />}
                      {contractStatus.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(rentalAmount)}
                  </TableCell>
                  <TableCell className="text-right font-medium text-green-600">
                    {formatCurrency(amountPaid)}
                  </TableCell>
                  <TableCell className={`text-right font-medium ${amountOwing > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(amountOwing)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleSendEmail(e, renter)}
                        title="Send Email"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/admin/renters/${renter.id}`)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleConvertToInquiry(e, renter)}
                        title="Convert to Inquiry"
                        className="text-amber-600 hover:text-amber-700"
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDelete(e, renter)}
                        title="Delete"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )})
            )}
          </TableBody>
          {filteredRenters.length > 0 && (
            <TableFooter>
              <TableRow className="bg-gray-50 font-semibold">
                <TableCell colSpan={4} className="text-right">
                  Totals ({filteredRenters.length} renters)
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(totals.rental)}
                </TableCell>
                <TableCell className="text-right text-green-600">
                  {formatCurrency(totals.paid)}
                </TableCell>
                <TableCell className={`text-right ${totals.owing > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(totals.owing)}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>

      {/* Add Renter Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Renter</DialogTitle>
          </DialogHeader>
          <RenterForm
            onSave={handleFormSave}
            onCancel={() => setShowAddDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
